import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { PencilIcon, ArrowLeftIcon, TrashIcon, MapPinIcon, CheckIcon, XMarkIcon } from '@heroicons/react/20/solid'
import { createCrudApi } from '../../lib/api'
import { nullToUndefined } from '../../lib/utils'
import { ContactForm } from '../../components/forms/ContactForm'
import { Avatar } from '../../components/ui/Avatar'
import { useTranslation } from '../../hooks/useTranslation'
import { useUserLookup } from '../../hooks/useUsers'
import { useFls } from '../../hooks/useFls'
import { RecordSharePanel } from '../../components/sharing/RecordSharePanel'
import { AuditHistoryPanel } from '../../components/audit/AuditHistoryPanel'
import type { Contact, ContactInput, Account, Address } from '../../../shared/types'

const contactsApi = createCrudApi<Contact, ContactInput>('contacts')
const accountsApi = createCrudApi<Account, unknown>('accounts')
const addressesApi = createCrudApi<Address, unknown>('addresses')

function formatAddress(address: Address): string {
  return `${address.street}, ${address.city}${address.state ? `, ${address.state}` : ''} ${address.postalCode || ''}, ${address.country}`
}

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [isEditingAddress, setIsEditingAddress] = useState(false)
  const [editUseAccountAddress, setEditUseAccountAddress] = useState(true)
  const [editMailingAddressId, setEditMailingAddressId] = useState<string | null>(null)
  const { t } = useTranslation()
  const { getUser } = useUserLookup()
  const { canRead, canWrite } = useFls('contacts')

  const { data: contact, isLoading } = useQuery({
    queryKey: ['contacts', id],
    queryFn: () => contactsApi.get(id!),
    enabled: !!id,
  })

  const { data: account } = useQuery({
    queryKey: ['accounts', contact?.accountId],
    queryFn: () => accountsApi.get(contact!.accountId),
    enabled: !!contact?.accountId,
  })

  // Fetch mailing address (either contact's own or account's primary)
  const { data: mailingAddress } = useQuery({
    queryKey: ['addresses', contact?.mailingAddressId],
    queryFn: () => addressesApi.get(contact!.mailingAddressId!),
    enabled: !!contact?.mailingAddressId && !contact?.useAccountAddress,
  })

  // Always fetch account addresses for the inline editor
  const { data: accountAddressesResponse } = useQuery({
    queryKey: ['addresses', 'byAccount', contact?.accountId],
    queryFn: () => addressesApi.list({ filters: { accountId: contact!.accountId }, limit: 50 }),
    enabled: !!contact?.accountId,
  })

  const accountAddresses = accountAddressesResponse?.data || []
  const accountPrimaryAddress = accountAddresses.find(a => a.type === 'primary')
  const displayAddress = contact?.useAccountAddress ? accountPrimaryAddress : mailingAddress

  const updateMutation = useMutation({
    mutationFn: (data: Partial<ContactInput>) => contactsApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', id] })
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      setIsEditing(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => contactsApi.delete(id!),
    onSuccess: () => {
      navigate('/contacts')
    },
  })

  // Address update mutation
  const updateAddressMutation = useMutation({
    mutationFn: (data: { useAccountAddress: boolean; mailingAddressId: string | null }) =>
      contactsApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', id] })
      queryClient.invalidateQueries({ queryKey: ['addresses'] })
      setIsEditingAddress(false)
    },
  })

  const startEditingAddress = () => {
    setEditUseAccountAddress(contact?.useAccountAddress ?? true)
    setEditMailingAddressId(contact?.mailingAddressId ?? null)
    setIsEditingAddress(true)
  }

  const saveAddressEdit = () => {
    updateAddressMutation.mutate({
      useAccountAddress: editUseAccountAddress,
      mailingAddressId: editUseAccountAddress ? null : editMailingAddressId,
    })
  }

  const cancelAddressEdit = () => {
    setIsEditingAddress(false)
  }

  if (isLoading) return <div>{t('common.loading')}</div>
  if (!contact) return <div>{t('pages.contacts.notFound')}</div>

  return (
    <>
      {/* Secondary column (desktop sidebar) */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="mb-6">
          <Link
            to="/contacts"
            className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            {t('pages.contacts.backToList')}
          </Link>
        </div>

        <div className="mb-6">
          <div className="flex items-center gap-x-4">
            <Avatar firstName={contact.firstName} lastName={contact.lastName} size="xl" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {contact.firstName} {contact.lastName}
              </h1>
              <p className="mt-1 text-sm text-gray-500">{contact.title || t('nav.contacts')}</p>
            </div>
          </div>
        </div>

        <div className="space-y-3 mb-6">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
          >
            <PencilIcon className="h-4 w-4" />
            {isEditing ? t('common.cancel') : t('common.edit')}
          </button>
          <button
            onClick={() => {
              if (confirm(t('pages.contacts.confirmDelete'))) {
                deleteMutation.mutate()
              }
            }}
            className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
          >
            <TrashIcon className="h-4 w-4" />
            {t('common.delete')}
          </button>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">{t('common.quickInfo')}</h2>
          <dl className="space-y-4">
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.email')}</dt>
              <dd className="mt-1 text-sm text-gray-900">{contact.email || '-'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.phone')}</dt>
              <dd className="mt-1 text-sm text-gray-900">{contact.phone || '-'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.title')}</dt>
              <dd className="mt-1 text-sm text-gray-900">{contact.title || '-'}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.account')}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {account ? (
                  <Link to={`/accounts/${account.id}`} className="text-brand-600 hover:text-brand-500">
                    {account.name}
                  </Link>
                ) : '-'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.mailingAddress')}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {displayAddress ? (
                  <div className="flex items-start gap-1">
                    <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                    <span>
                      {displayAddress.street}, {displayAddress.city}
                      {displayAddress.state && `, ${displayAddress.state}`}
                      {displayAddress.postalCode && ` ${displayAddress.postalCode}`}, {displayAddress.country}
                      {contact.useAccountAddress && (
                        <span className="text-xs text-gray-500 block">({t('fields.account')})</span>
                      )}
                    </span>
                  </div>
                ) : '-'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('fields.owner')}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {(() => {
                  const owner = getUser(contact.ownerId)
                  if (!owner) return '-'
                  return (
                    <div className="flex items-center gap-x-2">
                      <Avatar firstName={owner.firstName} lastName={owner.lastName} size="xs" />
                      <span>{owner.firstName} {owner.lastName}</span>
                    </div>
                  )
                })()}
              </dd>
            </div>
          </dl>
        </div>

        {/* Record Sharing */}
        <RecordSharePanel entity="contacts" entityId={id!} ownerId={contact.ownerId ?? undefined} />

        {/* Audit History */}
        <AuditHistoryPanel entity="contacts" entityId={id!} />
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] p-6">
        {/* Mobile header */}
        <div className="xl:hidden mb-6">
          <div className="mb-4">
            <Link
              to="/contacts"
              className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              {t('pages.contacts.backToList')}
            </Link>
          </div>
          <div className="flex gap-x-3">
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="inline-flex items-center gap-x-2 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
            >
              <PencilIcon className="h-4 w-4" />
              {isEditing ? t('common.cancel') : t('common.edit')}
            </button>
            <button
              onClick={() => {
                if (confirm(t('pages.contacts.confirmDelete'))) {
                  deleteMutation.mutate()
                }
              }}
              className="inline-flex items-center gap-x-2 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
            >
              <TrashIcon className="h-4 w-4" />
              {t('common.delete')}
            </button>
          </div>
        </div>

        {/* Detail card */}
        <div className="overflow-hidden bg-white shadow sm:rounded-lg">
          <div className="px-4 py-6 sm:px-6">
            <div className="flex items-center gap-x-4">
              <Avatar firstName={contact.firstName} lastName={contact.lastName} size="lg" />
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  {contact.firstName} {contact.lastName}
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">{t('pages.contacts.detailSubtitle')}</p>
              </div>
            </div>
          </div>

          {isEditing ? (
            <div className="border-t border-gray-100 px-4 py-6 sm:px-6">
              <ContactForm
                defaultValues={nullToUndefined(contact)}
                onSubmit={(data) => updateMutation.mutate(data)}
                onCancel={() => setIsEditing(false)}
                loading={updateMutation.isPending}
                accountName={account?.name}
                canWriteField={canWrite}
              />
            </div>
          ) : (
            <div className="border-t border-gray-100">
              <dl className="divide-y divide-gray-100">
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.fullName')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {contact.firstName} {contact.lastName}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.email')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {contact.email || '-'}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.phone')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {contact.phone || '-'}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.title')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {contact.title || '-'}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900">{t('fields.account')}</dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {account ? (
                      <Link to={`/accounts/${account.id}`} className="text-brand-600 hover:text-brand-500">
                        {account.name}
                      </Link>
                    ) : '-'}
                  </dd>
                </div>
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-900 flex items-center gap-2">
                    {t('fields.mailingAddress')}
                    {!isEditing && !isEditingAddress && (
                      <button
                        onClick={startEditingAddress}
                        className="text-gray-400 hover:text-gray-600"
                        title="Edit address"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    )}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {isEditingAddress ? (
                      <div className="space-y-3 bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="editUseAccountAddress"
                            checked={editUseAccountAddress}
                            onChange={(e) => setEditUseAccountAddress(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                          />
                          <label htmlFor="editUseAccountAddress" className="ml-2 text-sm text-gray-700">
                            {t('fields.useAccountAddress')}
                          </label>
                        </div>

                        {!editUseAccountAddress && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('fields.selectAddress')}
                            </label>
                            <select
                              value={editMailingAddressId || ''}
                              onChange={(e) => setEditMailingAddressId(e.target.value || null)}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                            >
                              <option value="">Select an address...</option>
                              {accountAddresses.map((address) => (
                                <option key={address.id} value={address.id}>
                                  [{address.type}] {formatAddress(address)}
                                </option>
                              ))}
                            </select>
                            {accountAddresses.length === 0 && (
                              <p className="mt-1 text-xs text-gray-500">
                                No addresses found. Add addresses on the account page first.
                              </p>
                            )}
                          </div>
                        )}

                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={saveAddressEdit}
                            disabled={updateAddressMutation.isPending}
                            className="inline-flex items-center gap-1 rounded-md bg-brand-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                          >
                            <CheckIcon className="h-3.5 w-3.5" />
                            {updateAddressMutation.isPending ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={cancelAddressEdit}
                            className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                          >
                            <XMarkIcon className="h-3.5 w-3.5" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : displayAddress ? (
                      <div className="flex items-start gap-2">
                        <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5 shrink-0" />
                        <div>
                          <p>{displayAddress.street}</p>
                          <p>
                            {displayAddress.city}
                            {displayAddress.state && `, ${displayAddress.state}`}
                            {displayAddress.postalCode && ` ${displayAddress.postalCode}`}
                          </p>
                          <p>{displayAddress.country}</p>
                          {contact.useAccountAddress && (
                            <p className="text-xs text-gray-500 mt-1">
                              (Using account's primary address)
                            </p>
                          )}
                        </div>
                      </div>
                    ) : '-'}
                  </dd>
                </div>
              </dl>
            </div>
          )}
        </div>
        </div>
      </div>
    </>
  )
}
